import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getShippingAddresses, createShippingAddress, updateShippingAddress, deleteShippingAddress } from '@/api/address';
import AddressItem from '@/components/AddressItem';
import AddressEditor from '@/components/AddressEditor';
import Button from '@/components/Button';
import ConfirmDialog from '@/components/ConfirmDialog';
import { MAX_ADDRESSES } from '@/utils/constants';
import addIcon from '@/assets/add_black.svg';
import emptyIcon from '@/assets/dondon_grey.svg';

const AddressPage = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [defaultAddressId, setDefaultAddressId] = useState(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [currentAddress, setCurrentAddress] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLimitDialogOpen, setIsLimitDialogOpen] = useState(false);
  
  const { data: addressesData, isLoading, isError } = useQuery({
    queryKey: ['shipping-addresses'],
    queryFn: getShippingAddresses
  });

  // Use useEffect to set default address when data changes
  useEffect(() => {
    if (addressesData?.data) {
      const defaultAddress = addressesData.data.find(address => address.isDefault);
      if (defaultAddress) {
        setDefaultAddressId(defaultAddress.id);
      } else {
        setDefaultAddressId(null);
      }
    }
  }, [addressesData]);

  const createAddressMutation = useMutation({
    mutationFn: createShippingAddress,
    onSuccess: () => {
      queryClient.invalidateQueries(['shipping-addresses']);
    }
  });

  const updateAddressMutation = useMutation({
    mutationFn: ({ id, data }) => updateShippingAddress(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['shipping-addresses']);
    }
  });

  const deleteAddressMutation = useMutation({
    mutationFn: (id) => deleteShippingAddress(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['shipping-addresses']);
    }
  });

  const addresses = addressesData?.data || [];

  const handleAddNewAddress = () => {
    if (addresses.length >= MAX_ADDRESSES) {
      setIsLimitDialogOpen(true);
      return;
    }
    
    setCurrentAddress(null);
    setIsEditing(false);
    setIsEditorOpen(true);
  };

  const handleSetDefault = (address) => {
    // Update address with isDefault set to true
    updateAddressMutation.mutate({
      id: address.id,
      data: {
        ...address,
        isDefault: true
      }
    });
  };

  const handleEditAddress = (address) => {
    setCurrentAddress(address);
    setIsEditing(true);
    setIsEditorOpen(true);
  };

  const handleDeleteAddress = (address) => {
    if (window.confirm(t('address.confirmDelete'))) {
      deleteAddressMutation.mutate(address.id);
    }
  };

  const handleCloseEditor = () => {
    setIsEditorOpen(false);
  };

  const handleSaveAddress = (formData, addressId) => {
    if (addressId) {
      // Update existing address
      updateAddressMutation.mutate({ id: addressId, data: formData });
    } else {
      // Create new address
      createAddressMutation.mutate(formData);
    }
    
    // Close the editor after saving
    setIsEditorOpen(false);
  };

  const handleLimitDialogClose = () => {
    setIsLimitDialogOpen(false);
  };

  return (
    <div className="w-full flex flex-col justify-start items-start bg-white rounded-lg shadow-sm p-6"> 
      {/* Header */}
      <div className="self-stretch flex justify-between items-center">
        <h1 className="text-lg font-bold">{t('me.menu.address')}</h1>
        <Button 
          onClick={handleAddNewAddress}
          variant="default"
          size="s"
          className="px-3.5"
          childContainerClassName="gap-0.5"
        >
          <img src={addIcon} alt="add" className="w-3.5 h-3.5" />
          <span>{t('address.add')}</span>
        </Button>
      </div>

      {/* Address List */}
      <div className="self-stretch py-6 flex justify-start items-start gap-4 flex-wrap">
        {isLoading ? (
          <div className="w-full flex justify-center items-center py-10">
            <p>{t('common.loading')}</p>
          </div>
        ) : isError ? (
          <div className="w-full flex justify-center items-center py-10">
            <p>{t('common.error')}</p>
          </div>
        ) : addresses.length > 0 ? (
          <div className='w-full grid grid-cols-2 gap-4'>
            {addresses.map((address) => (
              <AddressItem
                key={address.id}
                address={address}
                isDefault={address.id === defaultAddressId}
                onSetDefault={handleSetDefault}
                onEdit={handleEditAddress}
                onDelete={handleDeleteAddress}
              />
            ))}
          </div>
        ) : (
          <div className="w-full h-[600px] flex flex-col justify-center items-center">
            <img src={emptyIcon} alt="No addresses" className="w-[120px] h-[80px] mb-4" />
            <span className="text-gray-400">{t('address.empty')}</span>
          </div>
        )}
      </div>

      {/* Address Editor Modal */}
      <AddressEditor
        isOpen={isEditorOpen}
        onClose={handleCloseEditor}
        onSave={handleSaveAddress}
        address={currentAddress}
        isEditing={isEditing}
      />

      {/* Address Limit Dialog */}
      <ConfirmDialog
        isOpen={isLimitDialogOpen}
        onClose={handleLimitDialogClose}
        onConfirm={handleLimitDialogClose}
        title={t('address.limitReached', '地址已达上限（10 个）')}
        description={t('address.limitDescription', '如需添加新地址，请先删除已有的地址。')}
        confirmText={t('common.ok', '好的')}
      />
    </div>
  );
};

export default AddressPage; 